import {yupResolver} from "@hookform/resolvers/yup";
import React, {useEffect} from "react";
import {FieldErrors, FormProvider, useForm} from "react-hook-form";
import * as Yup from "yup";

import {Divider} from "@/components/divider/Divider.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {STATUS_PAGE_COMPONENTS_AND_METRICS_URL} from "@/lib/links.ts";
import {ServerErrors} from "@/lib/types/common.ts";
import {Component} from "@/lib/types/statuspages.ts";
import {MultiStatusPageComponentSelect} from "@/pages/statuspages/tabs/components/componentGroupModal/subComponents/MultiStatusPageComponentSelect.tsx";

export interface FormValues {
  id?: number;
  name: string;
  description: string;
  subcomponents?: number[];
}

const FormSchema = Yup.object({
  id: Yup.number().optional(),
  name: Yup.string().required("This field is required"),
  description: Yup.string().defined(),
  subcomponents: Yup.array().of(Yup.number().required()).optional(),
});

export type FormValuesKey = keyof FormValues;

interface FormProps {
  component?: Component;
  statusPageId: number;
  onSubmit: (d: FormValues) => void;
  serverErrors?: ServerErrors;
  onError?: (errors: FieldErrors<FormValues>, event?: React.BaseSyntheticEvent) => void;
  id?: string;
  defaultComponents?: Component[];
}

const StatusPageUpdateGroupComponentForm = ({
  id,
  component,
  statusPageId,
  onSubmit,
  onError,
  serverErrors,
  defaultComponents,
}: FormProps) => {
  const methods = useForm<FormValues>({
    resolver: yupResolver<FormValues>(FormSchema),
    defaultValues: {
      id: component?.id,
      name: component?.name,
      description: component?.description,
      subcomponents: component?.subcomponents?.map((c) => c.id) || [],
    },
  });

  const {register, handleSubmit, setError, setValue} = methods;

  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors).forEach((key) => {
        setError(key as FormValuesKey, {message: serverErrors[key][0]});
      });
    }
  }, [setError, serverErrors]);

  return (
    <FormProvider {...methods}>
      <form id={id} onSubmit={handleSubmit(onSubmit, onError)}>
        {serverErrors?.__all__ && <FormErrorAlerts errors={serverErrors?.__all__} />}
        {component && <input type="number" hidden {...register("id")} />}
        <div className={"d-flex flex-column gap-4"}>
          <div className="pulseTextBody">
            A Component Group organizes related Components to make it easy for your visitors to inspect. &nbsp;
            <a target="_blank" href={STATUS_PAGE_COMPONENTS_AND_METRICS_URL}>
              View Documentation
            </a>
          </div>
          <InputField
            name={"name"}
            label={"Group Name"}
            tooltip={"Group related Components under this heading"}
            required
            autoFocus
          />
          <InputField
            name={"description"}
            label={"Group Description"}
            tooltip={"OPTIONAL: Add a description visible to Page visitors beneath the name of the Component Group."}
          />
          <Divider />
          <MultiStatusPageComponentSelect
            statusPageId={statusPageId}
            defaultValue={component?.subcomponents?.map((c) => c.id) || []}
            defaultComponents={defaultComponents}
            onAppend={(id) => {
              const currentSubcomponents = methods.getValues("subcomponents") || [];
              setValue("subcomponents", [...currentSubcomponents, id]);
            }}
            onReplace={(ids) => {
              setValue("subcomponents", ids);
            }}
          />
        </div>
      </form>
    </FormProvider>
  );
};

export default StatusPageUpdateGroupComponentForm;
